import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../../utils/supabaseClient';
import Modal from '../../components/Modal';

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;

export default function Support() {
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const chatRef = useRef(null);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.lang = 'en-US';
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(prev => (prev ? prev + ' ' + transcript : transcript));
        setListening(false);
      };

      recognition.onerror = () => setListening(false);
      recognition.onend = () => setListening(false);

      recognitionRef.current = recognition;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (!user || error) {
        console.error(error || 'No user');
        return;
      }

      setUserId(user.id);

      const { data: sessionData } = await supabase.auth.getSession();
      sessionRef.current = sessionData.session;

      const { data: account } = await supabase
        .from('accounts')
        .select('name')
        .eq('id', user.id)
        .maybeSingle();

      const displayName =
        account?.name?.trim() ||
        user.user_metadata?.name?.trim() ||
        user.email?.split('@')[0] ||
        'friend';

      setName(displayName);

      const { data, error: msgError } = await supabase
        .from('chat_support')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (!msgError && data?.length) {
        setMessages(data.map(m => ({ sender: m.sender, text: m.text })));
      } else {
        const welcome = {
          sender: 'cici',
          text: `Hi, ${displayName}! 👋 How can I assist you? 😊`,
        };
        await supabase.from('chat_support').insert([{
          user_id: user.id,
          sender: welcome.sender,
          text: welcome.text,
        }]);
        setMessages([welcome]);
      }
    };

    init();
  }, []);

  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || !userId) return;

    const userMsg = { sender: 'user', text: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    await supabase.from('chat_support').insert([{
      user_id: userId,
      sender: userMsg.sender,
      text: userMsg.text,
    }]);

    const historyForAI = [
      ...messages.map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      })),
      { role: 'user', content: input.trim() },
    ];

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      sessionRef.current = sessionData.session;

      const token = sessionRef.current?.access_token;

      const res = await fetch('/api/support-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messages: historyForAI }),
      });

      const data = await res.json();

      if (!res.ok || !data.reply) {
        console.error('Error from server:', data.error);
        throw new Error(data.error || 'Failed to get reply');
      }

      const ciciMsg = { sender: 'cici', text: data.reply };
      setMessages(prev => [...prev, ciciMsg]);

      await supabase.from('chat_support').insert([{
        user_id: userId,
        sender: ciciMsg.sender,
        text: ciciMsg.text,
      }]);
    } catch (err) {
      console.error(err);
      const errorMsg = {
        sender: 'cici',
        text: "❌ Sorry, I couldn’t process your request. Please try again later. 🛠️"
      };
      setMessages(prev => [...prev, errorMsg]);
      await supabase.from('chat_support').insert([{
        user_id: userId,
        sender: errorMsg.sender,
        text: errorMsg.text,
      }]);
    }

    setLoading(false);
  };

  const handleMicClick = () => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      alert("Sorry, your browser doesn’t support speech recognition.");
      return;
    }
    if (listening) {
      recognition.stop();
      setListening(false);
    } else {
      setListening(true);
      recognition.start();
    }
  };

  const handleClearHistory = async () => {
    const { error } = await supabase
      .from('chat_support')
      .delete()
      .eq('user_id', userId);

    let msg;
    if (error) {
      console.error(error);
      msg = { sender: 'cici', text: "❌ Failed to clear chat history. Please try again." };
    } else {
      msg = { sender: 'cici', text: `Hi, ${name || 'friend'}! 👋 How can I assist you? 😊` };
    }

    await supabase.from('chat_support').insert([{
      user_id: userId,
      sender: msg.sender,
      text: msg.text,
    }]);

    setMessages([msg]);
    setShowModal(false);
  };

  const hasHistory = messages.length > 1;

  return (
    <>
      <header>
        <div className="section-title">
          <h1>Ask Vee</h1>
        </div>
        <div className="breadcrumbs">
          {hasHistory && (
            <button
              onClick={() => setShowModal(true)}
              className='btn btn-outlined'
              style={{ marginLeft: '1rem' }}
            >
              Clear Chat History
            </button>
          )}
        </div>
      </header>

      <section className='scroll scroll-chat'>
        <div className="contain" ref={chatRef} style={{ overflowY: 'auto' }}>
          <div className="cici-output">
            {messages.map((m, i) => (
              <div
                key={i}
                className={m.sender === 'cici' ? 'cici-text' : 'user-text'}
              >
                <p>{m.text}</p>
              </div>
            ))}
            {loading && (
              <div className="cici-text">
                <p>Typing…</p>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form className="cici-input" onSubmit={handleSubmit}>
            <input
              type="text"
              name="prompt"
              placeholder="Type your question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              required
            />
            <button
              type="button"
              className={`btn ${listening ? 'listening' : ''}`}
              onClick={handleMicClick}
              title="Speak"
            >
              <i className="bi bi-mic"></i>
            </button>
            <button type="submit" className="btn" title="Send">
              <i className="bi bi-arrow-up"></i>
            </button>
          </form>
        </div>
      </section>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Clear Chat History"
        actions={[
          {
            label: 'Clear',
            className: 'btn btn-outlined btn-delete',
            onClick: handleClearHistory,
          },
          {
            label: 'Cancel',
            className: 'btn btn-outlined',
            onClick: () => setShowModal(false),
          },
        ]}
      >
        <p>This will delete all your previous chat messages. Are you sure?</p>
      </Modal>
    </>
  );
}
